import { Road } from '../domain/types.ts';
import { GAME_CONFIG } from '../config/gameConfig.ts';

export type CustomerArchetype = 'passerby' | 'student' | 'office' | 'rider';

export interface VisualCustomer {
  id: string;
  roadId: string;
  segmentIndex: number; // Chỉ số đoạn đường hiện tại trên polyline
  direction: 1 | -1;    // Đi xuôi hay ngược polyline của con đường
  t: number;            // [0, 1] Tiến độ trên đoạn segment hiện tại
  x: number;
  y: number;
  speed: number;        // Tốc độ di chuyển
  archetype: CustomerArchetype;
  color: string;
  state: 'walking' | 'entering' | 'seated' | 'leaving' | 'takeaway';
  stallId?: string | undefined;
  seatedUntil?: number | undefined;
  takeawayUntil?: number | undefined;
  hasOrdered?: boolean | undefined;
  chatBubble?: {
    text: string;
    expiresAt: number;
  } | undefined;
}

export class CustomerAggregateManager {
  private customers: VisualCustomer[] = [];
  private maxVisibleCustomers = 85;
  private lastGossipTime = 0;

  public update(
    deltaMs: number,
    roads: Road[],
    ownedStallLocations: Array<{ id: string; x: number; y: number; chairs?: number }>,
    perks: string[] = [],
    currentShift: string = 'morning',
    weatherType: string = 'sunny',
    trendingTopic?: string,
    rivalLocations: Array<{ id: string; x: number; y: number; name: string; status: string }> = []
  ): VisualCustomer[] {
    const dt = deltaMs / 1000;
    const now = Date.now();

    // Map nhanh tìm road theo id
    const roadMap = new Map<string, Road>();
    for (const r of roads) roadMap.set(r.id, r);

    for (let i = this.customers.length - 1; i >= 0; i--) {
      const c = this.customers[i]!;

      // 1a. Xử lý khách đang mua mang đi (Takeaway: dừng lại 1.5s nhận đồ rồi đi tiếp)
      if (c.state === 'takeaway') {
        if (now >= (c.takeawayUntil ?? 0)) {
          c.state = 'walking';
          c.stallId = undefined;
          c.hasOrdered = undefined;
        }
        continue; // Dừng tại chỗ chờ lấy nước
      }

      // 1b. Nếu đang ngồi uống nước tại quán
      if (c.state === 'seated') {
        if (c.seatedUntil && now >= c.seatedUntil) {
          c.state = 'walking';
          c.stallId = undefined;
          c.hasOrdered = undefined;
          c.chatBubble = undefined;
        }
        continue;
      }

      // 2. Di chuyển CHÍNH XÁC bám theo Polyline của con đường
      const road = roadMap.get(c.roadId);
      if (!road || road.points.length < 2) {
        // Con đường bị xóa hoặc không hợp lệ -> loại bỏ
        this.customers.splice(i, 1);
        continue;
      }

      const p1Index = c.direction === 1 ? c.segmentIndex : c.segmentIndex + 1;
      const p2Index = c.direction === 1 ? c.segmentIndex + 1 : c.segmentIndex;

      const p1 = road.points[p1Index];
      const p2 = road.points[p2Index];

      if (!p1 || !p2) {
        // Hết con đường -> Đổi chiều hoặc chuyển đường mới
        c.direction = c.direction === 1 ? -1 : 1;
        c.segmentIndex = c.direction === 1 ? 0 : road.points.length - 2;
        c.t = 0;
        continue;
      }

      const segmentLen = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      if (segmentLen < 0.1) {
        // Bỏ qua đoạn quá ngắn
        c.segmentIndex += c.direction;
        c.t = 0;
        continue;
      }

      // Tiến độ trên segment hiện tại: dt * speed / segmentLen
      c.t += (dt * c.speed) / segmentLen;

      if (c.t >= 1.0) {
        // Đã đi hết đoạn hiện tại -> chuyển sang đoạn tiếp theo
        c.t = 0;
        const nextSeg = c.segmentIndex + c.direction;
        if (nextSeg < 0 || nextSeg >= road.points.length - 1) {
          // Hết đường, quay đầu đi ngược lại
          c.direction = c.direction === 1 ? -1 : 1;
          c.segmentIndex = Math.max(0, Math.min(road.points.length - 2, c.segmentIndex));
        } else {
          c.segmentIndex = nextSeg;
        }
      }

      // Tính tọa độ nội suy chính xác nằm trên lòng/vỉa hè đường
      const interpP1 = road.points[c.segmentIndex]!;
      const interpP2 = road.points[c.segmentIndex + 1]!;
      const progress = c.direction === 1 ? c.t : 1 - c.t;

      c.x = interpP1.x + (interpP2.x - interpP1.x) * progress;
      c.y = interpP1.y + (interpP2.y - interpP1.y) * progress;

      // 3. Kiểm tra xem có đi ngang qua quán của người chơi không để ghé vào hoặc mua mang đi
      if (ownedStallLocations.length > 0) {
        for (const stall of ownedStallLocations) {
          const distToStall = Math.hypot(c.x - stall.x, c.y - stall.y);
          if (distToStall < 24) {
            // Cạnh tranh tiệm dummy trên đường lớn: Đường lớn có nhiều tiệm cóc dummy cạnh tranh
            const isMajorRoad = road.roadClass === 'primary' || road.roadClass === 'secondary';
            if (isMajorRoad && Math.random() < (GAME_CONFIG.economy.dummyCompetitionRatio ?? 0.45)) {
              // Khách ghé các tiệm cóc dummy đối diện hoặc quầy hàng rong xung quanh
              continue;
            }

            const currentSeated = this.customers.filter((other) => other.stallId === stall.id && other.state === 'seated').length;
            const maxChairs = stall.chairs ?? 4;

            let seatChance = 0.18;
            if (perks.includes('perk-chess')) {
              seatChance *= 1.2;
            }
            if (perks.includes('perk-wifi') && (currentShift === 'noon' || currentShift === 'afternoon')) {
              seatChance *= 1.25;
            }
            if (perks.includes('perk-speaker') && currentShift === 'evening') {
              seatChance *= 1.5;
            }
            if (weatherType === 'hot' && !perks.includes('perk-mist')) {
              seatChance *= 0.8;
            }

            // Cạnh tranh trực tiếp từ các quán dummy lân cận (chia sẻ lượng khách vãng lai)
            let rivalPenalty = 0;
            for (const r of rivalLocations) {
              const d = Math.hypot(stall.x - r.x, stall.y - r.y);
              if (d < 180) {
                if (r.status === 'booming') rivalPenalty += 0.20;
                else if (r.status === 'stable') rivalPenalty += 0.10;
                else if (r.status === 'struggling') rivalPenalty += 0.04;
              }
            }
            seatChance *= Math.max(0.55, 1 - rivalPenalty);

            if (currentSeated < maxChairs && Math.random() < seatChance) {
              // Tạt vào ngồi uống nước
              c.state = 'seated';
              c.stallId = stall.id;
              const durationMult = perks.includes('perk-chess') ? 1.3 : 1.0;
              c.seatedUntil = now + (4000 + Math.random() * 5000) * durationMult; // Ngồi 4-9s (lâu hơn nếu có cờ tướng)
              c.hasOrdered = false; // Đánh dấu vừa ngồi, kích hoạt order món
              break;
            } else if (!c.hasOrdered) {
              // Cơ chế Mua Mang Đi (Take-away): Khách vãng lai đi ngang có 10-16% tỷ lệ mua mang đi thực tế
              const isPeakTakeaway = currentShift === 'morning' || currentShift === 'noon';
              const takeawayChance = isPeakTakeaway ? 0.16 : 0.10;
              if (Math.random() < takeawayChance) {
                c.state = 'takeaway';
                c.stallId = stall.id;
                c.takeawayUntil = now + 1500; // Dừng lại 1.5 giây lấy nước
                c.hasOrdered = false; // Kích hoạt thanh toán đơn hàng
                break;
              }
            }
          }
        }
      }
    }

    // 3b. Xử lý bong bóng tán chuyện cho khách ngồi (Google Trends Gossip)
    if (trendingTopic && now - this.lastGossipTime > 4500) {
      const seatedCustomers = this.customers.filter((cust) => cust.state === 'seated');
      if (seatedCustomers.length > 0) {
        const eligible = seatedCustomers.filter((cust) => !cust.chatBubble || cust.chatBubble.expiresAt < now);
        if (eligible.length > 0) {
          const picked = eligible[Math.floor(Math.random() * eligible.length)]!;
          const cleanTopic = trendingTopic.length > 25 ? `${trendingTopic.slice(0, 22)}...` : trendingTopic;
          const reactions = [
            `Vụ ${cleanTopic} hot thật đấy!`,
            `Bà trà đá thạo tin ${cleanTopic} ghê!`,
            `Mới đọc tin ${cleanTopic} sáng nay!`,
            `Vụ ${cleanTopic} bàn tán khắp phố!`,
          ];
          picked.chatBubble = {
            text: reactions[Math.floor(Math.random() * reactions.length)]!,
            expiresAt: now + 4000,
          };
          this.lastGossipTime = now + Math.random() * 1500;
        }
      }
    }

    // 4. Bổ sung nhân vật spawn trực tiếp trên các tuyến đường
    if (this.customers.length === 0 && roads.length > 0) {
      this.seedInitialCustomers(roads, 45);
    } else if (this.customers.length < this.maxVisibleCustomers && roads.length > 0) {
      const spawnCount = this.customers.length < 40 ? Math.floor(Math.random() * 3) + 3 : 1;
      for (let i = 0; i < spawnCount && this.customers.length < this.maxVisibleCustomers; i++) {
        this.spawnOnRoad(roads);
      }
    }

    return this.customers;
  }

  private spawnOnRoad(roads: Road[]): void {
    const validRoads = roads.filter((r) => r.points.length >= 2);
    if (validRoads.length === 0) return;

    const road = validRoads[Math.floor(Math.random() * validRoads.length)]!;
    const maxSegment = road.points.length - 2;
    const segmentIndex = Math.floor(Math.random() * (maxSegment + 1));
    const direction: 1 | -1 = Math.random() > 0.5 ? 1 : -1;
    const t = Math.random();

    const p1 = road.points[segmentIndex]!;
    const p2 = road.points[segmentIndex + 1]!;

    const x = p1.x + (p2.x - p1.x) * t;
    const y = p1.y + (p2.y - p1.y) * t;

    const archetypes: CustomerArchetype[] = ['passerby', 'student', 'office', 'rider'];
    const chosenArchetype = archetypes[Math.floor(Math.random() * archetypes.length)]!;

    let color = '#FFCA28'; // Vàng cam (Passerby)
    let speed = 18;        // Đi bộ vừa phải, ngắm phố

    if (chosenArchetype === 'student') {
      color = '#42A5F5';   // Xanh học sinh
      speed = 22;
    } else if (chosenArchetype === 'office') {
      color = '#AB47BC';   // Tím công sở
      speed = 16;
    } else if (chosenArchetype === 'rider') {
      color = '#26A69A';   // Xanh shipper
      speed = 32;
    }

    this.customers.push({
      id: `cust-${Date.now()}-${Math.random()}`,
      roadId: road.id,
      segmentIndex,
      direction,
      t,
      x,
      y,
      speed,
      archetype: chosenArchetype,
      color,
      state: 'walking',
    });
  }

  private seedInitialCustomers(roads: Road[], count: number = 45): void {
    const validRoads = roads.filter((r) => r.points.length >= 2);
    if (validRoads.length === 0) return;

    for (let i = 0; i < count; i++) {
      this.spawnOnRoad(validRoads);
    }
  }
}
